import React, { useState } from 'react';

import { useFetchCertificationsQuery } from 'app/apis/reviews';
import { Modal } from 'app/common/components/modal';
import plagiarismCaseQueueActions from 'app/queue/domains/plagiarism-case-queue/plagiarism-case-queue-actions';
import { getCompletedCertifications } from 'app/queue/domains/certifications/certifications-helpers';
import {
  selectPlagiarismCaseQueueEnabled,
  selectPlagiarismCaseQueueExpiration,
  selectProjectsEnabledInPlagiarismCaseQueue,
  selectAssignedPlagiarismCases,
} from 'app/queue/domains/plagiarism-case-queue/plagiarism-case-queue-selectors';

import { PlagiarismCaseQueueSettings } from 'app/queue/views/overview/queue/plagiarism-queue/plagiarism-case-queue-settings';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { QueueControlRow } from 'app/queue/views/overview/queue/queue-control-row';
import { useAppDispatch } from 'app/store';

export const PlagiarismQueue: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [isPlagiarismCaseOptionsModalOpen, setPlagiarismCaseOptionsModalOpen] = useState(false);

  const plagiarismQueueEnabled = useSelector(selectPlagiarismCaseQueueEnabled);
  const plagiarismQueueExpiresAt = useSelector(selectPlagiarismCaseQueueExpiration);
  const projectsEnabledInQueue = useSelector(selectProjectsEnabledInPlagiarismCaseQueue);
  const assignedPlagiarismCases = useSelector(selectAssignedPlagiarismCases);
  const { data: certifications } = useFetchCertificationsQuery();
  const plagiarismCaseCertifications = getCompletedCertifications(certifications) ?? [];

  const togglePlagiarismCaseQueue = () => {
    if (plagiarismQueueEnabled) {
      dispatch(plagiarismCaseQueueActions.leaveQueue());
    } else {
      setPlagiarismCaseOptionsModalOpen(true);
    }
  };

  const submitPlagiarismCaseQueueSettings = (projectIds: number[]) => {
    if (plagiarismQueueEnabled) {
      dispatch(plagiarismCaseQueueActions.updateRequests(projectIds));
    } else {
      dispatch(plagiarismCaseQueueActions.joinQueue(projectIds));
    }
    setPlagiarismCaseOptionsModalOpen(false);
  };

  const refreshPlagiarismCaseQueue = () => {
    dispatch(plagiarismCaseQueueActions.refreshRequests());
  };

  const disablePlagiarismCaseQueueButton = !plagiarismQueueEnabled && assignedPlagiarismCases.length > 0;

  return (
    <>
      <QueueControlRow
        name={t('common.plagiarismCases')}
        onOptionsClick={() => setPlagiarismCaseOptionsModalOpen(true)}
        onQueueClick={togglePlagiarismCaseQueue}
        onRefreshClick={refreshPlagiarismCaseQueue}
        isQueueEnabled={plagiarismQueueEnabled}
        expiration={plagiarismQueueExpiresAt}
        disableQueueButton={disablePlagiarismCaseQueueButton}
        disableQueueButtonTooltip={t('queue.overview.completeAssignedPlagiarismCase')}
      />

      <Modal
        ariaLabel={t('queue.overview.plagiarismCaseQueueSettings')}
        onClose={() => setPlagiarismCaseOptionsModalOpen(false)}
        isOpen={isPlagiarismCaseOptionsModalOpen}
      >
        <PlagiarismCaseQueueSettings
          isOptionsUpdate={plagiarismQueueEnabled}
          projectsEnabledInQueue={projectsEnabledInQueue}
          plagiarismCaseCertifications={plagiarismCaseCertifications}
          onSubmit={submitPlagiarismCaseQueueSettings}
        />
      </Modal>
    </>
  );
};
